import { resolveStep } from "./inputStep";

const MAX_TICKS = 11;

interface SliderTickSource {
    min_value?: number | null;
    max_value?: number | null;
    step?: number | null;
    scale_options?: ReadonlyArray<{ value?: unknown; label?: unknown }> | null;
}

export interface SliderTick {
    value: number;
    label: string;
    /** The words of a "10 = Worst possible" label, shown under the slider's end when it is the min or max. */
    anchor?: string;
}

/** Splits a rating label such as "10 = Worst possible" (from a dictionary) into the number and its words. */
function splitAnchor(value: number, label: string): Pick<SliderTick, "label" | "anchor"> {
    const match = label.match(/^\s*(-?\d+(?:\.\d+)?)\s*=\s*(.+?)\s*$/);
    if (!match || Number(match[1]) !== value) return { label };
    return { label: match[1]!, anchor: match[2]! };
}

/**
 * A label under every value of the slider, when its scale_options label each value from min to max
 * (at most 11, e.g. 0-10). Sliders that only label their ends keep the end labels, so nothing changes
 * for them. A label like "10 = Worst possible" shows its number, with the words kept as the tick's anchor.
 * Returns null when ticks do not apply.
 */
export function sliderTicks(input: SliderTickSource): SliderTick[] | null {
    const min = input.min_value ?? 0;
    const max = input.max_value ?? 100;
    const step = resolveStep(input.step ?? null, 1);
    if (!(step > 0) || max <= min) return null;
    const count = Math.round((max - min) / step) + 1;
    if (count > MAX_TICKS) return null;

    const labels = new Map<number, string>();
    for (const option of input.scale_options ?? []) {
        const value = Number(option.value);
        if (Number.isFinite(value) && typeof option.label === "string") labels.set(value, option.label);
    }

    const ticks: SliderTick[] = [];
    for (let index = 0; index < count; index++) {
        const value = Number((min + index * step).toFixed(6));
        const label = labels.get(value);
        if (label === undefined) return null;
        ticks.push({ value, ...splitAnchor(value, label) });
    }
    return ticks;
}

/** The anchor words under the slider's two ends (e.g. "None" and "Worst possible"); null when neither has one. */
export function sliderEndAnchors(ticks: readonly SliderTick[] | null): { min?: string; max?: string } | null {
    const min = ticks?.[0]?.anchor;
    const max = ticks?.[ticks.length - 1]?.anchor;
    return min || max ? { min, max } : null;
}

/**
 * Every tick with words, as "n = words", split by which end of the slider it is nearer: the low half under the
 * left end, the high half under the right. A dictionary like Rateing_1 ("0 = None", "1 = Very mild",
 * "10 = Worst possible") then shows "1 = Very mild" under "0 = None". Sliders with words on the ends only get
 * one line each side, as before. Null when no tick has words.
 */
export function sliderAnchorLines(ticks: readonly SliderTick[] | null): { low: string[]; high: string[] } | null {
    if (!ticks?.length) return null;
    const middle = (ticks[0]!.value + ticks[ticks.length - 1]!.value) / 2;
    const low: string[] = [];
    const high: string[] = [];
    for (const tick of ticks) {
        if (!tick.anchor) continue;
        (tick.value <= middle ? low : high).push(`${tick.label} = ${tick.anchor}`);
    }
    return low.length || high.length ? { low, high } : null;
}
